// src/components/ArbitrageSection.jsx
import React, { useState } from "react";
import "../styles/ArbitrageSection.css";

const ArbitrageSection = ({ data, onSelectLegOdds }) => {
  const [totalStake, setTotalStake] = useState(100);
  const [expandedArb, setExpandedArb] = useState(null);

  if (!data) return null;

  const { opportunities, best_odds, total_markets_scanned, warning } = data;

  const asNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const formatMoney = (value) => {
    const n = asNumber(value);
    return n == null ? "—" : `£${n.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    const n = asNumber(value);
    return n == null ? "—" : `${n.toFixed(2)}%`;
  };

  const calculateStakes = (arb, total) => {
    if (arb.type === "2-way") {
      return {
        leg1: ((arb.leg1.stake_pct / 100) * total).toFixed(2),
        leg2: ((arb.leg2.stake_pct / 100) * total).toFixed(2),
      };
    } else {
      return {
        leg1: ((arb.leg1.stake_pct / 100) * total).toFixed(2),
        leg2: ((arb.leg2.stake_pct / 100) * total).toFixed(2),
        leg3: ((arb.leg3.stake_pct / 100) * total).toFixed(2),
      };
    }
  };

  const formatBookmaker = (name) => {
    if (!name) return name;
    // Capitalize and clean up bookmaker names
    return name
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getCalculatorView = (arb) => {
    const calc = arb?.calculator || {};
    const calcStake = asNumber(calc.total_stake);
    const calcProfit = asNumber(calc.guaranteed_profit);
    const calcReturn = asNumber(calc.guaranteed_return);
    const calcRoi = asNumber(calc.roi_pct);

    const fallbackStake = asNumber(totalStake) ?? 0;
    const fallbackProfitPct = asNumber(arb?.profit_pct) ?? 0;
    const fallbackProfit = (fallbackProfitPct / 100) * fallbackStake;
    const fallbackReturn = fallbackStake + fallbackProfit;

    return {
      totalStake: calcStake ?? fallbackStake,
      guaranteedReturn: calcReturn ?? fallbackReturn,
      guaranteedProfit: calcProfit ?? fallbackProfit,
      roiPct: calcRoi ?? fallbackProfitPct,
    };
  };

  const canPrefill = typeof onSelectLegOdds === "function";
  const handlePrefillOdds = (event, oddsValue) => {
    event.stopPropagation();
    if (!canPrefill) return;
    onSelectLegOdds(String(oddsValue ?? ""));
  };

  return (
    <div className="arb-section">
      {/* Header */}
      <div className="arb-header">
        <div className="arb-header-info">
          <span className="arb-markets-scanned">
            {total_markets_scanned} markets scanned
          </span>
        </div>
      </div>

      {/* Warning Banner */}
      {warning && (
        <div className="arb-warning">
          <span className="arb-warning-icon">⚠️</span>
          <span>{warning}</span>
        </div>
      )}

      {/* Opportunities */}
      {opportunities && opportunities.length > 0 ? (
        <div className="arb-opportunities">
          <h4 className="arb-opportunities-title">
            <span className="arb-found-badge">{opportunities.length}</span>
            Arbitrage {opportunities.length === 1 ? "Opportunity" : "Opportunities"} Found!
          </h4>

          {opportunities.map((arb, idx) => {
            const isExpanded = expandedArb === idx;
            const stakes = calculateStakes(arb, totalStake);
            const calcView = getCalculatorView(arb);
            const calcProfitPositive = (asNumber(calcView.guaranteedProfit) ?? 0) > 0;
            const profitPct = asNumber(arb?.profit_pct);

            return (
              <div
                key={idx}
                className={`arb-card ${isExpanded ? "expanded" : ""}`}
                onClick={() => setExpandedArb(isExpanded ? null : idx)}
              >
                <div className="arb-card-header">
                  <div className="arb-market">{arb.market}</div>
                  <div className="arb-profit-badge">
                    {profitPct != null ? `+${profitPct.toFixed(2)}%` : "—"}
                  </div>
                </div>

                <div className="arb-legs">
                  {/* Leg 1 */}
                  <div className="arb-leg">
                    <span className="arb-leg-selection">{arb.leg1.selection}</span>
                    <span className="arb-leg-bookmaker">
                      {formatBookmaker(arb.leg1.bookmaker)}
                    </span>
                    <span className="arb-leg-odds">@ {arb.leg1.odds}</span>
                    {canPrefill && (
                      <button
                        type="button"
                        className="arb-leg-prefill-btn"
                        onClick={(event) => handlePrefillOdds(event, arb.leg1.odds)}
                      >
                        Use in quick calc
                      </button>
                    )}
                  </div>

                  {/* Leg 2 */}
                  <div className="arb-leg">
                    <span className="arb-leg-selection">{arb.leg2.selection}</span>
                    <span className="arb-leg-bookmaker">
                      {formatBookmaker(arb.leg2.bookmaker)}
                    </span>
                    <span className="arb-leg-odds">@ {arb.leg2.odds}</span>
                    {canPrefill && (
                      <button
                        type="button"
                        className="arb-leg-prefill-btn"
                        onClick={(event) => handlePrefillOdds(event, arb.leg2.odds)}
                      >
                        Use in quick calc
                      </button>
                    )}
                  </div>

                  {/* Leg 3 (3-way only) */}
                  {arb.leg3 && (
                    <div className="arb-leg">
                      <span className="arb-leg-selection">{arb.leg3.selection}</span>
                      <span className="arb-leg-bookmaker">
                        {formatBookmaker(arb.leg3.bookmaker)}
                      </span>
                      <span className="arb-leg-odds">@ {arb.leg3.odds}</span>
                      {canPrefill && (
                        <button
                          type="button"
                          className="arb-leg-prefill-btn"
                          onClick={(event) => handlePrefillOdds(event, arb.leg3.odds)}
                        >
                          Use in quick calc
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded Stake Calculator */}
                {isExpanded && (
                  <div className="arb-calculator" onClick={(e) => e.stopPropagation()}>
                    <div className="arb-calc-summary">
                      <div className="arb-calc-summary-title">Calculator</div>
                      <div className="arb-calc-summary-grid">
                        <div className="arb-calc-item">
                          <span className="arb-calc-label">Total Stake</span>
                          <span className="arb-calc-value">{formatMoney(calcView.totalStake)}</span>
                        </div>
                        <div className="arb-calc-item">
                          <span className="arb-calc-label">Guaranteed Return</span>
                          <span className="arb-calc-value">{formatMoney(calcView.guaranteedReturn)}</span>
                        </div>
                        <div className="arb-calc-item">
                          <span className="arb-calc-label">Guaranteed Profit</span>
                          <span className={`arb-calc-value ${calcProfitPositive ? "is-positive" : "is-neutral"}`}>
                            {formatMoney(calcView.guaranteedProfit)}
                          </span>
                        </div>
                        <div className="arb-calc-item">
                          <span className="arb-calc-label">ROI</span>
                          <span className="arb-calc-value">{formatPercent(calcView.roiPct)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="arb-calc-header">
                      <label>Total Stake:</label>
                      <div className="arb-stake-input-wrap">
                        <span className="arb-currency">£</span>
                        <input
                          type="number"
                          value={totalStake}
                          onChange={(e) => setTotalStake(Number(e.target.value) || 0)}
                          className="arb-stake-input"
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="arb-stakes-breakdown">
                      <div className="arb-stake-row">
                        <span>{arb.leg1.selection}</span>
                        <span className="arb-stake-amount">£{stakes.leg1}</span>
                      </div>
                      <div className="arb-stake-row">
                        <span>{arb.leg2.selection}</span>
                        <span className="arb-stake-amount">£{stakes.leg2}</span>
                      </div>
                      {arb.leg3 && (
                        <div className="arb-stake-row">
                          <span>{arb.leg3.selection}</span>
                          <span className="arb-stake-amount">£{stakes.leg3}</span>
                        </div>
                      )}
                      <div className="arb-stake-row arb-profit-row">
                        <span>Guaranteed Profit</span>
                        <span className="arb-profit-amount">
                          £{(((asNumber(arb?.profit_pct) ?? 0) / 100) * totalStake).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="arb-card-footer">
                  <span className="arb-expand-hint">
                    {isExpanded ? "Click to collapse" : "Click for stake calculator"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="arb-empty">
          <div className="arb-empty-icon">📊</div>
          <div className="arb-empty-title">No Arbitrage Opportunities</div>
          <div className="arb-empty-text">
            The odds across bookmakers are currently too aligned for arbitrage.
            Check back closer to kickoff when odds typically diverge more.
          </div>
        </div>
      )}

      {/* Best Odds Summary (optional, collapsed by default) */}
      {best_odds && Object.keys(best_odds).length > 0 && (
        <details className="arb-best-odds-details">
          <summary className="arb-best-odds-summary">
            View Best Odds Per Market
          </summary>
          <div className="arb-best-odds">
            {Object.entries(best_odds)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([market, info]) => (
                <div key={market} className="arb-best-odd-row">
                  <span className="arb-best-market">{market}</span>
                  <span className="arb-best-bookmaker">
                    {formatBookmaker(info.bookmaker)}
                  </span>
                  <span className="arb-best-price">{info.price}</span>
                </div>
              ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default ArbitrageSection;
